import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell, RequireAdmin } from './AdminShell';
import { blogApi } from '../../lib/adminApi';
import { Plus, Pencil, Trash2, ExternalLink, Loader2 } from 'lucide-react';

const StatusPill = ({ status }) => {
  const map = {
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    draft: 'bg-slate-100 text-slate-600 border-slate-200',
    scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center h-6 px-2.5 rounded-full border text-[10.5px] font-bold uppercase tracking-[0.12em] ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

const AdminBlog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    blogApi.list(false).then(setPosts).catch((e) => setErr(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const removePost = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This can't be undone.`)) return;
    await blogApi.remove(slug);
    load();
  };

  const togglePublish = async (post) => {
    const next = post.status === 'published' ? 'draft' : 'published';
    await blogApi.update(post.slug, { ...post, status: next });
    load();
  };

  return (
    <RequireAdmin>
      <AdminShell
        title="Blog posts"
        actions={
          <Link
            to="/admin/blog/new" data-testid="admin-new-post"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300"
          >
            <Plus className="h-4 w-4" /> New post
          </Link>
        }
      >
        {err && <p className="text-rose-600 text-[13px] mb-4">{err}</p>}
        {loading ? (
          <div className="flex items-center gap-2 text-body-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-100 p-10 text-center">
            <p className="text-navy font-bold text-[18px]">No posts yet</p>
            <p className="mt-2 text-body-muted text-[14px]">Create your first post to get started.</p>
            <Link
              to="/admin/blog/new"
              className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-brand-pink text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk"
            >
              <Plus className="h-4 w-4" /> Create post
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
            <table className="w-full text-left text-[14px]">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.12em] text-body-muted">
                <tr>
                  <th className="py-3 px-5 font-bold">Title</th>
                  <th className="py-3 px-5 font-bold">Category</th>
                  <th className="py-3 px-5 font-bold">Status</th>
                  <th className="py-3 px-5 font-bold">Updated</th>
                  <th className="py-3 px-5 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {posts.map((p) => (
                  <tr key={p.id} data-testid={`admin-row-${p.slug}`} className="hover:bg-slate-50">
                    <td className="py-3.5 px-5">
                      <p className="font-bold text-navy">{p.title}</p>
                      <p className="text-body-muted text-[12.5px] mt-0.5">/{p.slug}</p>
                    </td>
                    <td className="py-3.5 px-5 text-body-muted">{p.category}</td>
                    <td className="py-3.5 px-5"><StatusPill status={p.status} /></td>
                    <td className="py-3.5 px-5 text-body-muted text-[13px]">
                      {new Date(p.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button" onClick={() => togglePublish(p)}
                          data-testid={`admin-toggle-${p.slug}`}
                          className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-blue hover:underline"
                        >
                          {p.status === 'published' ? 'Unpublish' : 'Publish'}
                        </button>
                        <a
                          href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-body-muted hover:text-navy hover:bg-slate-100"
                          title="View"
                        ><ExternalLink className="h-4 w-4" /></a>
                        <button
                          type="button" onClick={() => navigate(`/admin/blog/${p.slug}/edit`)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-body-muted hover:text-navy hover:bg-slate-100"
                          title="Edit"
                        ><Pencil className="h-4 w-4" /></button>
                        <button
                          type="button" onClick={() => removePost(p.slug)}
                          data-testid={`admin-delete-${p.slug}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-rose-500 hover:bg-rose-50"
                          title="Delete"
                        ><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </RequireAdmin>
  );
};

export default AdminBlog;
